import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ShieldCheck, ShieldX, Hourglass, Award, Briefcase, Calendar, ThumbsUp, ThumbsDown } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin "Pending Approvals" page — lists every approval_flag record with
 * inline approve/reject. Approving an entry locks-in the contractor's
 * loyalty uplift % and dispatches a notification back to the contractor.
 */
export default function PendingApprovalsPage() {
  const { api } = useAuth();
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);
  const [decisionNotes, setDecisionNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const { data } = await api.get(`/approval-flags${params}`);
      setFlags(Array.isArray(data) ? data : []);
    } catch { setFlags([]); }
    setLoading(false);
  }, [api, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const syncAll = async () => {
    try {
      const { data } = await api.post('/approval-flags/sync');
      toast.success(`Scanned ${data.contractors_scanned} contractors for new milestones.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
  };

  const decide = async (flag, kind) => {
    setBusyId(flag._id);
    try {
      const url = `/approval-flags/${flag._id}/${kind}`;
      const body = { notes: decisionNotes[flag._id] || '' };
      await api.post(url, body);
      toast.success(kind === 'approve' ? 'Milestone approved — contractor notified.' : 'Milestone rejected.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    setBusyId(null);
  };

  return (
    <div data-testid="approvals-page" className="space-y-6 p-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck size={20} className="text-[#D35400]" /> Pending Approvals
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Contractor loyalty milestones. Approve to lock in their uplift % and notify them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="approvals-status-filter"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-2 py-1.5 border border-[#E2E2DF] rounded-md text-sm bg-white"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>
          <button
            data-testid="approvals-sync-btn"
            onClick={syncAll}
            className="text-sm font-semibold px-3 py-1.5 rounded-md border border-[#D35400] text-[#D35400] hover:bg-[#D35400] hover:text-white"
          >Sync milestones</button>
        </div>
      </header>

      <div className="bg-white border border-[#E2E2DF] rounded-xl overflow-hidden">
        {loading ? (
          <p className="text-center text-sm text-slate-400 py-12">Loading…</p>
        ) : flags.length === 0 ? (
          <div className="text-center py-12">
            <Hourglass size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">No {statusFilter} approvals right now.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E2E2DF] bg-slate-50/60">
                <th className="text-left text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 py-2 px-3">Contractor</th>
                <th className="text-left text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 py-2 px-3">Milestone</th>
                <th className="text-left text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 py-2 px-3">Raised</th>
                <th className="text-left text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 py-2 px-3">Notes</th>
                <th className="text-right text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 py-2 px-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {flags.map(f => (
                <tr key={f._id} data-testid={`approval-row-${f._id}`} className="border-b border-slate-50">
                  <td className="py-2 px-3">
                    <p className="font-semibold text-slate-800">{f.contractor_name || '—'}</p>
                    <p className="text-[11px] text-slate-400">{f.contractor_id.slice(-8)}</p>
                  </td>
                  <td className="py-2 px-3">
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.1em] text-[#D35400]">
                      {f.kind === 'tenure' ? <Calendar size={11} /> : <Briefcase size={11} />}
                      {f.kind === 'tenure' ? `${f.milestone_value} year${f.milestone_value === 1 ? '' : 's'}` : `${f.milestone_value} jobs`}
                    </span>
                    {f.status === 'approved' && (
                      <p className="text-[10px] text-emerald-600 font-semibold mt-0.5"><Award size={9} className="inline-block -mt-0.5" /> Locked at {(f.locked_loyalty_percent || 0).toFixed(1)}%</p>
                    )}
                  </td>
                  <td className="py-2 px-3 text-xs text-slate-500">{new Date(f.created_at).toLocaleString()}</td>
                  <td className="py-2 px-3">
                    {f.status === 'pending' ? (
                      <input
                        data-testid={`approval-notes-${f._id}`}
                        value={decisionNotes[f._id] || ''}
                        onChange={e => setDecisionNotes(n => ({ ...n, [f._id]: e.target.value }))}
                        placeholder="optional notes"
                        className="w-full px-2 py-1 text-xs border border-slate-200 rounded"
                      />
                    ) : (
                      <p className="text-xs text-slate-500 italic">{f.notes || '—'}</p>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {f.status === 'pending' ? (
                      <div className="inline-flex gap-1.5">
                        <button
                          data-testid={`approval-approve-${f._id}`}
                          onClick={() => decide(f, 'approve')}
                          disabled={busyId === f._id}
                          className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-50"
                        ><ThumbsUp size={11} /> Approve</button>
                        <button
                          data-testid={`approval-reject-${f._id}`}
                          onClick={() => decide(f, 'reject')}
                          disabled={busyId === f._id}
                          className="flex items-center gap-1 px-2 py-1 rounded bg-slate-500 text-white text-xs font-bold hover:bg-slate-600 disabled:opacity-50"
                        ><ThumbsDown size={11} /> Reject</button>
                      </div>
                    ) : f.status === 'approved' ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-700">
                        <ShieldCheck size={11} /> Approved
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                        <ShieldX size={11} /> Rejected
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
